import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ISearchAPIPayload } from "../types/common";
import { IAgent } from "../../Components/agents/types";
import { PAGE_PROPS } from "../constants/common";
import { AGENTS_INITIAL_STATE } from "../constants/agents.constants";

const agentUrls = {
  list: {
    "Master Agents": "/api/MasterAgent/GetPagedMasterAgents",
    "Super Agents": "/api/SuperAgent/GetPagedSuperAgents",
    "Sub Agents": "/api/SubAgent/GetPagedSubAgents",
  },
  add: {
    "Master Agents": "/api/MasterAgent/RegisterMasterAgent",
    "Super Agents": "/api/SuperAgent/RegisterSuperAgent",
    "Sub Agents": "/api/SubAgent/RegisterSubAgent",
  },
  update: {
    "Master Agents": "/api/MasterAgent/UpdateMasterAgent",
    "Super Agents": "/api/SuperAgent/UpdateSuperAgent",
    "Sub Agents": "/api/SubAgent/UpdateSubAgent",
  },
  delete: {
    "Master Agents": "/api/MasterAgent/DeleteMasterAgent/",
    "Super Agents": "/api/SuperAgent/DeleteSuperAgent/",
    "Sub Agents": "/api/SubAgent/DeleteSubAgent/",
  },
  getAll: {
    "Master Agents": "/api/MasterAgent/GetAllMasterAgent",
    "Super Agents": "/api/SuperAgent/GetAllSuperAgent",
    "Sub Agents": "/api/SubAgent/GetAllSubAgent",
  },
  getInfo: {
    MasterAgent: "/api/Transaction/GetMasterAgentByUserId/",
    SuperAgent: "/api/Transaction/GetSuperAgentByUserId/",
    SubAgent: "/api/Transaction/GetSubAgentByUserId/",
  },
};

export const getAllPagedAgents = createAsyncThunk(
  "agents/getAllPaged",
  async (data: ISearchAPIPayload) => {
    const url = agentUrls.list[data.type as keyof typeof agentUrls.list];
    const response = await axios.post(url, data);
    return response.data;
  }
);

export const addAgent = createAsyncThunk(
  "agents/add",
  async (data: Partial<IAgent>) => {
    const url = agentUrls.add[data.type as keyof typeof agentUrls.list];

    const response = await axios.post(url, data);
    return response.data;
  }
);

export const updateAgent = createAsyncThunk(
  "agents/update",
  async (data: Partial<IAgent>) => {
    const url = agentUrls.update[data.type as keyof typeof agentUrls.list];

    const response = await axios.post(url, data);
    return response.data;
  }
);

export const deleteAgent = createAsyncThunk(
  "agents/delete",
  async (data: { id: number; type: string }) => {
    const url = agentUrls.delete[data.type as keyof typeof agentUrls.list];

    const response = await axios.post(url + data.id, {});
    return response.data;
  }
);

export const getAllSuperAgents = createAsyncThunk(
  "agents/getAllSuperAgents",
  async () => {
    const url = agentUrls.getAll["Super Agents"];
    const response = await axios.get(url);
    return response.data;
  }
);

export const getAllSubAgents = createAsyncThunk(
  "agents/getAllSubAgents",
  async () => {
    const url = agentUrls.getAll["Sub Agents"];
    const response = await axios.get(url);
    return response.data;
  }
);

export const getAgentByUserId = createAsyncThunk(
  "agents/getAgentByUserId",
  async (data: any) => {
    const url = agentUrls.getInfo[data?.type as keyof typeof agentUrls.getInfo];
    const response = await axios.get(url + data.userId);
    return response.data;
  }
);

export const agentsSlice = createSlice({
  name: "agents",
  initialState: AGENTS_INITIAL_STATE,
  reducers: {
    onChangeAgentsScreen: (state, action) => {
      state.currentScreen = action.payload;
    },
    onChangeAgentsList: (state, action) => {
      state.pageProps = action.payload;
    },
    onSelectAgent: (state, action) => {
      state.selectedAgent = action.payload;
    },
    onSelectAgentType: (state, action) => {
      state.agentType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllPagedAgents.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllPagedAgents.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload?.result ?? PAGE_PROPS;
        state.error = null;
      })
      .addCase(getAllPagedAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "An er ror occurred.";
      })
      .addCase(getAllSubAgents.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllSubAgents.fulfilled, (state, action) => {
        state.loading = false;
        state.subAgents = action.payload?.result ?? PAGE_PROPS;
        state.error = null;
      })
      .addCase(getAllSubAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "An er ror occurred.";
      })
      .addCase(addAgent.pending, (state) => {
        state.loading = true;
      })
      .addCase(addAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(addAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "An error occurred.";
      })
      .addCase(updateAgent.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "An error occurred.";
      })
      .addCase(deleteAgent.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "An error occurred.";
      })
      .addCase(getAllSuperAgents.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllSuperAgents.fulfilled, (state, action) => {
        state.loading = false;
        state.superAgents = action.payload?.result ?? PAGE_PROPS;
        state.error = null;
      })
      .addCase(getAllSuperAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "An er ror occurred.";
      })
      .addCase(getAgentByUserId.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAgentByUserId.fulfilled, (state, action) => {
        state.loading = false;
        state.loginAgentInfo = action.payload?.result ?? {};
        state.error = null;
      })
      .addCase(getAgentByUserId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "An er ror occurred.";
      });
  },
});

export const {
  onChangeAgentsScreen,
  onChangeAgentsList,
  onSelectAgent,
  onSelectAgentType,
} = agentsSlice.actions;

export default agentsSlice.reducer;
